// PayoutRouting.js - Intelligent Payout Routing Configuration for BlitzPay 9.0
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Slider,
  InputAdornment,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  AccountBalance as BankIcon,
  Payment as PayPalIcon,
  CreditCard as StripeIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { toast } from "sonner";
import useAuth from "../../Hooks/use-auth";

const PayoutRouting = () => {
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();

  // State
  const [enableAutoRouting, setEnableAutoRouting] = useState(true);
  const [defaultMethod, setDefaultMethod] = useState("gu_balance");
  const [routingRules, setRoutingRules] = useState([
    {
      id: 1,
      name: "Small Transfers to GU Balance",
      condition: "amount_less_than",
      value: 1000,
      method: "gu_balance",
      priority: 1,
      enabled: true,
    },
    {
      id: 2,
      name: "Large Transfers to PayPal",
      condition: "amount_greater_than",
      value: 1000,
      method: "paypal",
      priority: 2,
      enabled: true,
    },
  ]);

  const [showAddRuleDialog, setShowAddRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({
    name: "",
    condition: "amount_less_than",
    value: "",
    method: "gu_balance",
    enabled: true,
  });

  // Analytics State
  const [analytics, setAnalytics] = useState({
    totalPayouts: 234,
    guBalancePayouts: 187,
    externalPayouts: 47,
    feesSaved: 2847.50,
    avgProcessingTime: 12, // minutes
  });

  // Payment Methods
  const paymentMethods = [
    {
      id: "gu_balance",
      name: "GU Balance (Internal)",
      icon: <BankIcon />,
      fee: "0%",
      color: "#2e7d32",
      description: "Fee-free internal transfers",
    },
    {
      id: "paypal",
      name: "PayPal",
      icon: <PayPalIcon />,
      fee: "2.5%",
      color: "#0070ba",
      description: "1-2 day processing",
    },
    {
      id: "stripe",
      name: "Stripe",
      icon: <StripeIcon />,
      fee: "2.0%",
      color: "#635bff",
      description: "2-3 day processing",
    },
    {
      id: "ach",
      name: "Bank ACH",
      icon: <BankIcon />,
      fee: "1.0%",
      color: "#1976d2",
      description: "3-5 day processing",
    },
  ];

  // Condition Types
  const conditionTypes = [
    { value: "amount_less_than", label: "Amount less than" },
    { value: "amount_greater_than", label: "Amount greater than" },
    { value: "amount_between", label: "Amount between" },
    { value: "creator_preference", label: "Creator preference" },
    { value: "always", label: "Always use" },
  ];

  // Handle Add/Edit Rule
  const handleSaveRule = () => {
    if (!newRule.name || !newRule.value) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (editingRule) {
      // Update existing rule
      setRoutingRules(
        routingRules.map((rule) =>
          rule.id === editingRule.id
            ? { ...newRule, id: editingRule.id, priority: editingRule.priority }
            : rule
        )
      );
      toast.success("Rule updated successfully");
    } else {
      // Add new rule
      const newRuleWithId = {
        ...newRule,
        id: Date.now(),
        priority: routingRules.length + 1,
      };
      setRoutingRules([...routingRules, newRuleWithId]);
      toast.success("Rule added successfully");
    }

    // Reset form
    setNewRule({
      name: "",
      condition: "amount_less_than",
      value: "",
      method: "gu_balance",
      enabled: true,
    });
    setEditingRule(null);
    setShowAddRuleDialog(false);
  };

  // Handle Delete Rule
  const handleDeleteRule = (ruleId) => {
    setRoutingRules(routingRules.filter((rule) => rule.id !== ruleId));
    toast.success("Rule deleted successfully");
  };

  // Handle Edit Rule
  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setNewRule({
      name: rule.name,
      condition: rule.condition,
      value: rule.value,
      method: rule.method,
      enabled: rule.enabled,
    });
    setShowAddRuleDialog(true);
  };

  // Handle Toggle Rule
  const handleToggleRule = (ruleId) => {
    setRoutingRules(
      routingRules.map((rule) =>
        rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
      )
    );
  };

  // Save Configuration
  const handleSaveConfiguration = () => {
    // TODO: Implement API call to save routing configuration
    toast.success("Configuration saved successfully!", {
      description: "Routing rules have been updated",
      icon: <CheckCircleIcon />,
    });
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(value || 0);
  };

  // Get method info
  const getMethodInfo = (methodId) => {
    return paymentMethods.find((m) => m.id === methodId) || paymentMethods[0];
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <SettingsIcon fontSize="large" />
          Payout Routing Configuration
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configure intelligent routing rules to optimize payout costs and processing times
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Analytics Dashboard */}
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                <TrendingUpIcon />
                Routing Analytics
              </Typography>
              <Tooltip title="Refresh analytics">
                <IconButton size="small">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "primary.main", color: "white" }}>
                  <Typography variant="caption">Total Payouts</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {analytics.totalPayouts}
                  </Typography>
                  <Typography variant="caption">Last 30 days</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "success.main", color: "white" }}>
                  <Typography variant="caption">GU Balance</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {analytics.guBalancePayouts}
                  </Typography>
                  <Typography variant="caption">{((analytics.guBalancePayouts / analytics.totalPayouts) * 100).toFixed(1)}% of total</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "warning.main", color: "white" }}>
                  <Typography variant="caption">External Payouts</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {analytics.externalPayouts}
                  </Typography>
                  <Typography variant="caption">{((analytics.externalPayouts / analytics.totalPayouts) * 100).toFixed(1)}% of total</Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 2, textAlign: "center", bgcolor: "error.main", color: "white" }}>
                  <Typography variant="caption">Fees Saved</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {formatCurrency(analytics.feesSaved)}
                  </Typography>
                  <Typography variant="caption">vs external only</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Default Settings */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Default Settings
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={enableAutoRouting}
                  onChange={(e) => setEnableAutoRouting(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable Automatic Routing"
              sx={{ mb: 3 }}
            />

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When automatic routing is disabled, all payouts will use the default method below.
            </Typography>

            <TextField
              select
              fullWidth
              label="Default Payment Method"
              value={defaultMethod}
              onChange={(e) => setDefaultMethod(e.target.value)}
              disabled={!enableAutoRouting}
              sx={{ mb: 2 }}
            >
              {paymentMethods.map((method) => (
                <MenuItem key={method.id} value={method.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: method.color }}>{method.icon}</Box>
                    <Box>
                      <Typography variant="body2">{method.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {method.fee} fee • {method.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                <strong>Recommended:</strong> Set GU Balance as default to minimize fees.
                External methods will be used only when GU Balance is unavailable.
              </Typography>
            </Alert>
          </Card>
        </Grid>

        {/* Payment Methods Overview */}
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Payment Methods
            </Typography>

            <List>
              {paymentMethods.map((method, index) => (
                <React.Fragment key={method.id}>
                  <ListItem>
                    <Box sx={{ color: method.color, mr: 2 }}>{method.icon}</Box>
                    <ListItemText
                      primary={method.name}
                      secondary={method.description}
                    />
                    <ListItemSecondaryAction>
                      <Chip
                        label={method.fee}
                        size="small"
                        color={method.fee === "0%" ? "success" : "default"}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < paymentMethods.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Card>
        </Grid>

        {/* Routing Rules */}
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Routing Rules
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setShowAddRuleDialog(true)}
                disabled={!enableAutoRouting}
              >
                Add Rule
              </Button>
            </Box>

            {!enableAutoRouting && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Automatic routing is disabled. Enable it in Default Settings to use routing rules.
              </Alert>
            )}

            {routingRules.length === 0 ? (
              <Alert severity="info">
                No routing rules configured. Add rules to optimize your payout routing.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="50">Priority</TableCell>
                      <TableCell>Rule Name</TableCell>
                      <TableCell>Condition</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {routingRules
                      .sort((a, b) => a.priority - b.priority)
                      .map((rule) => {
                        const methodInfo = getMethodInfo(rule.method);
                        const conditionType = conditionTypes.find(c => c.value === rule.condition);

                        return (
                          <TableRow key={rule.id}>
                            <TableCell>
                              <Chip label={rule.priority} size="small" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {rule.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {conditionType?.label}: ${rule.value}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ color: methodInfo.color }}>{methodInfo.icon}</Box>
                                <Typography variant="body2">{methodInfo.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Switch
                                checked={rule.enabled}
                                onChange={() => handleToggleRule(rule.id)}
                                size="small"
                                disabled={!enableAutoRouting}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit rule">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditRule(rule)}
                                  disabled={!enableAutoRouting}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete rule">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteRule(rule.id)}
                                  disabled={!enableAutoRouting}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Grid>

        {/* Save Configuration Button */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button variant="outlined" size="large">
              Reset to Default
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveConfiguration}
            >
              Save Configuration
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Add/Edit Rule Dialog */}
      <Dialog
        open={showAddRuleDialog}
        onClose={() => {
          setShowAddRuleDialog(false);
          setEditingRule(null);
          setNewRule({
            name: "",
            condition: "amount_less_than",
            value: "",
            method: "gu_balance",
            enabled: true,
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingRule ? "Edit Routing Rule" : "Add New Routing Rule"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Rule Name"
              fullWidth
              value={newRule.name}
              onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
              placeholder="e.g., Small transfers to GU Balance"
              sx={{ mb: 2 }}
              required
            />

            <TextField
              select
              label="Condition"
              fullWidth
              value={newRule.condition}
              onChange={(e) => setNewRule({ ...newRule, condition: e.target.value })}
              sx={{ mb: 2 }}
            >
              {conditionTypes.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </TextField>

            {newRule.condition !== "always" && newRule.condition !== "creator_preference" && (
              <TextField
                label="Amount Threshold"
                type="number"
                fullWidth
                value={newRule.value}
                onChange={(e) => setNewRule({ ...newRule, value: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ mb: 2 }}
                required
              />
            )}

            <TextField
              select
              label="Payment Method"
              fullWidth
              value={newRule.method}
              onChange={(e) => setNewRule({ ...newRule, method: e.target.value })}
              sx={{ mb: 2 }}
            >
              {paymentMethods.map((method) => (
                <MenuItem key={method.id} value={method.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ color: method.color }}>{method.icon}</Box>
                    <Box>
                      <Typography variant="body2">{method.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {method.fee} fee
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <FormControlLabel
              control={
                <Switch
                  checked={newRule.enabled}
                  onChange={(e) => setNewRule({ ...newRule, enabled: e.target.checked })}
                />
              }
              label="Enable this rule"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setShowAddRuleDialog(false);
              setEditingRule(null);
              setNewRule({
                name: "",
                condition: "amount_less_than",
                value: "",
                method: "gu_balance",
                enabled: true,
              });
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveRule}>
            {editingRule ? "Update Rule" : "Add Rule"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayoutRouting;
